import React from "react";
import { PageHeader } from "../../field-service/components/PageHeader/PageHeader";
import { WOEmailsTab } from "../../field-service/components/WOEmailsTab/WOEmailsTab";

// ─── Types ────────────────────────────────────────────────────────────────────

type WOStatus = "OPEN" | "ONSITE" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "OVERDUE";

interface WorkOrderData {
  id: string;
  number: string;
  title: string;
  description: string;
  status: WOStatus;
  assignees: { initials: string; name: string }[];
  team: string;
  dueDate: string;
  schedule: string;
  companyContact: { initials: string; name: string };
  followers: { initials: string }[];
  resources: string[];
  createdAt: string;
  createdBy: { initials: string; name: string };
  company: { name: string; id: string };
  asset: { name: string; id: string };
}

export interface LinkedDoc {
  type: "quote" | "invoice";
  number: string;
  status: string;
  date: string;
}

export interface WOLineItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  price: number;
  discount: number;
}

interface TimeEntry {
  id: string;
  date: string;
  workType: string;
  category: string;
  billable: boolean;
  assignee: { initials: string; name: string };
  duration: string;
  timeRange: string;
}

type DetailTab = "Overview" | "Activity Log" | "Comments" | "Emails" | "Time Tracking";

interface WOChatMessage {
  id: string;
  sender: string;
  senderInitials: string;
  text: string;
  time: string;
  isOutgoing: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_WO: WorkOrderData = {
  id: "1",
  number: "WO-2026-0001",
  title: "Rotary Machine Spinner Repair",
  description: "Perform routine maintenance on the CNC Machine A including lubrication, calibration, and part replacement. Ensure all safety checks are completed before returning the machine to operation.",
  status: "OPEN",
  assignees: [{ initials: "JM", name: "James Mcdonald" }, { initials: "SP", name: "Sara Phillips" }, { initials: "BP", name: "Ben Parker" }],
  team: "TECHNICIAN",
  dueDate: "12/06/2026",
  schedule: "12/06/2026 - 04:30PM",
  companyContact: { initials: "JM", name: "James Mcdonald" },
  followers: [{ initials: "JM" }, { initials: "SP" }, { initials: "BP" }],
  resources: ["CUSTOM", "+1"],
  createdAt: "12/06/2026",
  createdBy: { initials: "JM", name: "James Mcdonald" },
  company: { name: "Acme International", id: "0000123" },
  asset: { name: "Acme Asset 2000", id: "00000XP" },
};

const MOCK_TIME_ENTRIES: TimeEntry[] = [
  {
    id: "te-1",
    date: "14/10/2025",
    workType: "On Site Work",
    category: "Machine Servicing",
    billable: true,
    assignee: { initials: "JM", name: "James Mcdonald" },
    duration: "0:30:00",
    timeRange: "12:00 PM - 12:30 PM",
  },
  {
    id: "te-2",
    date: "14/10/2025",
    workType: "On Site Work",
    category: "Calibration",
    billable: true,
    assignee: { initials: "SP", name: "Sara Phillips" },
    duration: "1:15:00",
    timeRange: "02:00 PM - 03:15 PM",
  },
  {
    id: "te-3",
    date: "15/10/2025",
    workType: "Remote Work",
    category: "Diagnostics",
    billable: false,
    assignee: { initials: "BP", name: "Ben Parker" },
    duration: "0:45:00",
    timeRange: "09:00 AM - 09:45 AM",
  },
];

const MOCK_PARTS = [
  { id: "1", name: "Oil Filter Kit",   articleNumber: "00011OFK", quantity: 2  },
  { id: "2", name: "Drive Belt",       articleNumber: "00012DB",  quantity: 1  },
  { id: "3", name: "Lubricant Spray",  articleNumber: "00013LS",  quantity: 3  },
  { id: "4", name: "Electrical Part",  articleNumber: "00014JP",  quantity: 20 },
];

// ─── Status Config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<WOStatus, { bg: string; color: string; label: string }> = {
  OPEN:        { bg: "#ccefeb", color: "#00b09b", label: "OPEN" },
  ONSITE:      { bg: "#fff0e6", color: "#ff7a00", label: "ONSITE" },
  IN_PROGRESS: { bg: "#fff0e6", color: "#ff7a00", label: "IN PROGRESS" },
  COMPLETED:   { bg: "#e6f9f1", color: "#10b981", label: "COMPLETED" },
  ON_HOLD:     { bg: "#f3f4f6", color: "#748094", label: "ON HOLD" },
  OVERDUE:     { bg: "#fde5f0", color: "#ee0064", label: "OVERDUE" },
};

const WO_COMMENT_MESSAGES: WOChatMessage[] = [
  {
    id: "c1", sender: "Michael Ziegler", senderInitials: "MZ",
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    time: "11:52 AM", isOutgoing: true,
  },
  {
    id: "c2", sender: "James McDonald", senderInitials: "JM",
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    time: "11:52 AM", isOutgoing: false,
  },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

function WOInfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#f97316" strokeWidth="1.4" />
      <path d="M8 7v4" stroke="#f97316" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.8" fill="#f97316" />
    </svg>
  );
}

function WOSendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2L7.5 8.5M14 2L9.5 14l-2-5.5L2 6.5l12-4.5z" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l5-5 5 5" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 7.5l5 5 5-5" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="16" cy="10" r="1.5" />
    </svg>
  );
}

function DotsHorizontalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--color-content-secondary)">
      <circle cx="3" cy="8" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="13" cy="8" r="1.5" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 10V3M5.5 7.5L8 10l2.5-2.5" />
      <path d="M3 11v1.5A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5V11" />
    </svg>
  );
}

function ChatBubbleIcon({ color = "var(--color-content-secondary)" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 8c0 3.314-2.686 6-6 6a6.06 6.06 0 01-2.47-.524L2 14.5l1.024-3.53A5.974 5.974 0 012 8c0-3.314 2.686-6 6-6s6 2.686 6 6z" />
    </svg>
  );
}

function RecordIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="8" r="2.5" fill="white" stroke="none" />
    </svg>
  );
}

function PlusIcon({ color = "#0517f8" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}

function DocumentIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="1.5" width="10" height="13" rx="1.5" />
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
    </svg>
  );
}

function ReceiptIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 1.5v13l2-1.5 2 1.5 2-1.5 2 1.5 2-1.5V1.5" />
      <path d="M6 5.5h4M6 8h4M6 10.5h2" />
    </svg>
  );
}

function StatusIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3.5l2 2" />
    </svg>
  );
}

function UserIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M2.5 14c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" />
    </svg>
  );
}

function UsersIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="5" r="2" />
      <path d="M1 14c0-2.761 2.239-5 5-5s5 2.239 5 5" />
      <path d="M11 7c.552 0 1.046.224 1.414.586M13.5 14c0-2.761-1.119-5-2.5-5" />
    </svg>
  );
}

function CalendarIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" />
      <path d="M1.5 6.5h13M5 1.5v2M11 1.5v2" />
    </svg>
  );
}

function IdIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
      <path d="M5 7h6M5 9.5h3.5" />
    </svg>
  );
}

function BuildingIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="12" height="10" rx="1" />
      <path d="M5 4V3a3 3 0 016 0v1M6 8h4" />
    </svg>
  );
}

function ClipboardIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 2.5h5a1 1 0 011 1v10a1 1 0 01-1 1h-7a1 1 0 01-1-1v-10a1 1 0 011-1h1" />
      <path d="M6 2.5a2 2 0 014 0" />
      <path d="M6 7h4M6 9.5h4M6 12h2" />
    </svg>
  );
}

function AttachmentIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 7.5l-6 6a3.5 3.5 0 01-4.95-4.95l6-6A2.333 2.333 0 0111.85 5.85L6 11.7a1.167 1.167 0 01-1.65-1.65l5.5-5.55" />
    </svg>
  );
}

function LinkIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 9.5l3-3M5 7l-1.5 1.5a2.828 2.828 0 104 4L9 11M11 9l1.5-1.5a2.828 2.828 0 10-4-4L7 5" />
    </svg>
  );
}

function AssetIconSmall({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <path d="M5 8h6M8 5v6" />
    </svg>
  );
}

function ClockIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 4.5V7l1.5 1.5" />
    </svg>
  );
}

function LockIcon({ color = "#748094" }: { color?: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5.5" width="9" height="6.5" rx="1.5" />
      <path d="M4 5.5V4a2.5 2.5 0 015 0v1.5" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.8 1.5H3.5A1.5 1.5 0 002 3v5.3c0 .4.16.78.44 1.06l5.2 5.2a1.5 1.5 0 002.12 0l4.24-4.24a1.5 1.5 0 000-2.12L8.8 1.5z" />
      <circle cx="5.5" cy="5.5" r="1" fill="#748094" stroke="none" />
    </svg>
  );
}

function EuroIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 6.5a5 5 0 100 7M4.5 9h7M4.5 11h7" />
    </svg>
  );
}

function AlertCircleIcon({ color = "#ff7a00" }: { color?: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="7" fill={color} />
      <path d="M7.5 4v4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="7.5" cy="10.5" r="0.8" fill="white" />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: WOStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: cfg.bg, color: cfg.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function Avatar({ initials, size = 20 }: { initials: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "#e6e8fe",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size <= 20 ? 9 : 10, fontWeight: 700, color: "#0517f8",
      fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px", flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function AvatarStack({ users }: { users: { initials: string }[] }) {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {users.map((u, i) => (
        <div key={i} style={{ marginLeft: i > 0 ? -4 : 0, position: "relative", zIndex: users.length - i }}>
          <Avatar initials={u.initials} />
        </div>
      ))}
    </div>
  );
}

// ─── Detail Row ───────────────────────────────────────────────────────────────

function DetailRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 32 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        width: 140, flexShrink: 0, minHeight: 32,
      }}>
        <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </div>
        <span style={{
          fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)",
          lineHeight: "16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", minHeight: 32 }}>
        {children}
      </div>
    </div>
  );
}

function DetailValueText({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      flex: 1, minWidth: 0,
      padding: "0 8px", height: 32, display: "flex", alignItems: "center",
    }}>
      <span style={{
        fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)",
        fontFamily: "var(--font-family-base)", lineHeight: "16px",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {children}
      </span>
    </div>
  );
}

// ─── Accordion ────────────────────────────────────────────────────────────────

function Accordion({
  title, defaultOpen = true, children, actions,
}: {
  title: string; defaultOpen?: boolean; children: React.ReactNode; actions?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ background: "white", border: "1px solid #e8eaed", borderRadius: 8, overflow: "hidden" }}>
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px", cursor: "pointer", userSelect: "none",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{
          fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
        }}>
          {title}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          {actions}
          <div onClick={() => setOpen((v) => !v)} style={{ cursor: "pointer" }}>
            {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </div>
      </div>
      {open && <div style={{ padding: "0 16px 16px" }}>{children}</div>}
    </div>
  );
}

// ─── Left column accordions ───────────────────────────────────────────────────

function DescriptionSection({ description }: { description: string }) {
  const [open, setOpen] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(description);
  return (
    <div style={{
      background: "white", border: "1px solid #e8eaed", borderRadius: 8,
      padding: 16, display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
        onClick={() => { if (!editing) setOpen((v) => !v); }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: "50%", background: "#f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round">
            <path d="M1.5 3h9M1.5 6h9M1.5 9h6" />
          </svg>
        </div>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
        }}>
          Description
        </span>
        <div style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms", flexShrink: 0 }}>
          <ChevronUpIcon />
        </div>
      </div>
      {open && (
        editing ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => setEditing(false)}
            autoFocus
            style={{
              width: "100%", minHeight: 72, padding: "8px",
              border: "1px solid var(--color-border-primary)", borderRadius: 6,
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 500,
              color: "var(--color-content-primary)", resize: "vertical",
              outline: "none", boxSizing: "border-box", lineHeight: "18px",
            }}
          />
        ) : (
          <p
            onClick={() => setEditing(true)}
            style={{
              fontSize: 12, fontWeight: 500,
              color: value ? "var(--color-content-primary)" : "#a2aab8",
              fontFamily: "var(--font-family-base)", lineHeight: "18px", margin: 0,
              cursor: "text",
            }}
          >
            {value || "Add a description…"}
          </p>
        )
      )}
    </div>
  );
}

type ProcedureStatus = "NOT_STARTED" | "FINALIZED" | "DRAFT";

const PROCEDURE_STATUS_CONFIG: Record<ProcedureStatus, { bg: string; color: string; label: string }> = {
  NOT_STARTED: { bg: "#f3f4f6", color: "#748094", label: "NOT STARTED" },
  FINALIZED:   { bg: "#ccefeb", color: "#00b09b", label: "FINALIZED" },
  DRAFT:       { bg: "#e6e8fe", color: "#0517f8", label: "DRAFT" },
};

const MOCK_PROCEDURES = [
  { id: "1", name: "Annual Maintenance Check", updatedBy: { initials: "JM", name: "James Mcdonald" }, status: "FINALIZED" as ProcedureStatus },
  { id: "2", name: "Safety Inspection Protocol", updatedBy: { initials: "SP", name: "Sara Phillips" }, status: "DRAFT" as ProcedureStatus },
];

function ProcedureIconSmall() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" />
      <path d="M3.5 5h5M3.5 7.5h3" />
    </svg>
  );
}

function ProcedureSection() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{
      background: "white", border: "1px solid #e8eaed", borderRadius: 8,
      padding: 16, display: "flex", flexDirection: "column", gap: 16,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 24, height: 24, borderRadius: 6, background: "#e6e8fe",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <ProcedureIconSmall />
        </div>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
        }}>
          Procedures
        </span>
        <button style={{
          border: "none", background: "transparent", cursor: "pointer", padding: "0 4px",
          fontSize: 12, fontWeight: 700, color: "#0517f8",
          fontFamily: "var(--font-family-base)", lineHeight: "16px",
        }}>
          + Assign
        </button>
        <div
          style={{ cursor: "pointer", transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 200ms", flexShrink: 0 }}
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronUpIcon />
        </div>
      </div>

      {/* Table */}
      {open && (
        <div style={{ border: "1px solid #e8eaed", borderRadius: 8, overflow: "hidden" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", background: "#f3f4f6", borderBottom: "1px solid #e8eaed" }}>
            <div style={{ flex: 2, padding: "8px 12px", minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>Procedure Name</span>
            </div>
            <div style={{ flex: 1, padding: "8px 12px", minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>Last Updated by</span>
            </div>
            <div style={{ width: 120, flexShrink: 0, padding: "8px 12px" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#748094", fontFamily: "var(--font-family-base)" }}>Status</span>
            </div>
            <div style={{ width: 40, flexShrink: 0 }} />
          </div>
          {/* Data rows */}
          {MOCK_PROCEDURES.map((proc) => {
            const sc = PROCEDURE_STATUS_CONFIG[proc.status];
            return (
              <div key={proc.id} style={{
                display: "flex", alignItems: "center", height: 48,
                borderTop: "1px solid #e8eaed",
              }}>
                <div style={{ flex: 2, padding: "0 12px", minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 4, background: "#e6e8fe",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <ProcedureIconSmall />
                  </div>
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {proc.name}
                  </span>
                </div>
                <div style={{ flex: 1, padding: "0 12px", minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar initials={proc.updatedBy.initials} size={20} />
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {proc.updatedBy.name}
                  </span>
                </div>
                <div style={{ width: 120, flexShrink: 0, padding: "0 12px", display: "flex", alignItems: "center" }}>
                  <span style={{
                    padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                    background: sc.bg, color: sc.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                  }}>
                    {sc.label}
                  </span>
                </div>
                <div style={{ width: 40, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
                    <DotsIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InventoryIcon({ size = 16, color = "#0517f8" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="5" width="13" height="9.5" rx="1.5" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.5 8.5h13" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.5 2v4.5M10.5 2v4.5" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="11" r="1" fill={color} />
    </svg>
  );
}

function PartRowIcon() {
  return (
    <div style={{
      width: 24, height: 24, borderRadius: 4,
      background: "#f3f4f6", border: "1px solid #e8eaed",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <InventoryIcon size={14} color="#a2aab8" />
    </div>
  );
}

function PartsSection() {
  const [open, setOpen] = React.useState(true);
  const parts = MOCK_PARTS;

  return (
    <div style={{
      background: "white", border: "1px solid #e8eaed", borderRadius: 8,
      padding: 16, display: "flex", flexDirection: "column", gap: 16,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, height: 24 }}>
        <div style={{ display: "flex", flex: 1, alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{
            width: 24, height: 24, borderRadius: "50%", background: "#e6e8fe",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <InventoryIcon size={16} color="#0517f8" />
          </div>
          <span style={{
            flex: 1, fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
          }}>
            Parts
          </span>
        </div>
        <button
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            border: "none", background: "transparent", cursor: "pointer",
            padding: 4, fontSize: 12, fontWeight: 700, color: "#0517f8",
            fontFamily: "var(--font-family-base)", lineHeight: "16px", letterSpacing: "-0.24px",
            whiteSpace: "nowrap",
          }}
        >
          <PlusIcon color="#0517f8" />
          Assign
        </button>
        <div
          style={{
            cursor: "pointer", flexShrink: 0,
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 200ms",
          }}
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronUpIcon />
        </div>
      </div>

      {/* Table */}
      {open && (
        <div style={{ border: "1px solid #e8eaed", borderRadius: 8, overflow: "hidden" }}>
          {/* Column headers */}
          <div style={{ display: "flex", background: "#f3f4f6" }}>
            <div style={{ width: "50%", flexShrink: 0, padding: "8px 8px" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px", whiteSpace: "nowrap" }}>
                Name
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px", whiteSpace: "nowrap" }}>
                Article Number
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0, padding: "8px 0" }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px", whiteSpace: "nowrap" }}>
                Quantity
              </span>
            </div>
            <div style={{ width: 40, flexShrink: 0 }} />
          </div>

          {/* Data rows */}
          {parts.map((part) => (
            <div key={part.id} style={{ display: "flex", alignItems: "center", borderTop: "1px solid #e8eaed" }}>
              {/* Name */}
              <div style={{
                width: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", gap: 8,
                padding: "12px 8px",
              }}>
                <PartRowIcon />
                <span style={{
                  flex: 1, fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)", lineHeight: "16px", letterSpacing: "-0.24px",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {part.name}
                </span>
              </div>
              {/* Article Number */}
              <div style={{ flex: 1, minWidth: 0, padding: "16px 0" }}>
                <span style={{
                  fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)", lineHeight: "16px",
                }}>
                  {part.articleNumber}
                </span>
              </div>
              {/* Quantity */}
              <div style={{ flex: 1, minWidth: 0, padding: "16px 0" }}>
                <span style={{
                  fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)", lineHeight: "16px",
                }}>
                  {part.quantity}
                </span>
              </div>
              {/* Actions */}
              <div style={{
                width: 40, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "16px 8px",
              }}>
                <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
                  <DotsHorizontalIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentsSection() {
  return (
    <Accordion title="Attachments" defaultOpen={false}>
      <div style={{ padding: "0 16px" }}>
        <button style={{
          display: "flex", alignItems: "center", gap: 6,
          border: "1px dashed var(--color-border-primary)", borderRadius: 8,
          background: "transparent", padding: "10px 14px", cursor: "pointer", width: "100%",
          fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
        }}>
          <PlusIcon /> Upload Attachment
        </button>
      </div>
    </Accordion>
  );
}

function LinkedWOsSection() {
  const linked = [
    { number: "WO-2026-0043", title: "Annual Inspection", status: "COMPLETED" as WOStatus },
    { number: "WO-2026-0042", title: "Filter Change", status: "OPEN" as WOStatus },
  ];
  return (
    <Accordion title="Linked Work Orders" defaultOpen={false}>
      <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {linked.map((wo) => (
          <div key={wo.number} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 12px", border: "1px solid var(--color-border-primary)", borderRadius: 8,
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>{wo.title}</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>{wo.number}</span>
            </div>
            <StatusBadge status={wo.status} />
          </div>
        ))}
        <button style={{
          display: "flex", alignItems: "center", gap: 6,
          border: "none", background: "transparent", padding: "4px 0", cursor: "pointer",
          fontSize: 12, fontWeight: 700, color: "#0517f8", fontFamily: "var(--font-family-base)",
        }}>
          <PlusIcon /> Link Work Order
        </button>
      </div>
    </Accordion>
  );
}

// ─── SLA Timer ────────────────────────────────────────────────────────────────

type SLARowState = "idle" | "running" | "met" | "not_met";

function SLARow({
  byLabel, dueLabel, deadlineStr,
  state, startedAt, mountElapsed = 0, targetMinutes = 0,
}: {
  byLabel: string; dueLabel: string; deadlineStr: string;
  state: SLARowState; startedAt?: number; mountElapsed?: number; targetMinutes?: number;
}) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (state !== "running") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  let timerContent: React.ReactNode;
  if (state === "idle") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 500, color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>—</span>
    );
  } else if (state === "met") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: "#00b09b", fontFamily: "var(--font-family-base)" }}>✓ Met</span>
    );
  } else if (state === "not_met") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: "#ee0064", fontFamily: "var(--font-family-base)" }}>✗ Not Met</span>
    );
  } else {
    const elapsed = mountElapsed + (startedAt != null ? (now - startedAt) / 60000 : 0);
    const remaining = targetMinutes - elapsed;
    const breached = remaining <= 0;
    const percentLeft = remaining / targetMinutes;
    const timerColor = breached ? "#ee0064" : percentLeft < 0.15 ? "#ee0064" : percentLeft < 0.35 ? "#ff7a00" : "#00b09b";
    const abs = Math.abs(remaining);
    const h = Math.floor(abs / 60);
    const m = Math.floor(abs % 60);
    const s = Math.floor((abs * 60) % 60);
    const countdown = `${breached ? "+" : ""}${h}h ${m}m ${String(s).padStart(2, "0")}s`;
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: timerColor, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
        {countdown}
      </span>
    );
  }

  return (
    <>
      {/* "Response by:" / "Resolution by:" row — lock icon + datetime */}
      <div style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 32 }}>
        <div style={{ width: 140, display: "flex", alignItems: "center", gap: 8, flexShrink: 0, padding: "8px 0" }}>
          <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.5" y="5.5" width="9" height="7" rx="1.5" stroke="#748094" strokeWidth="1.3"/>
              <path d="M4 5.5V4a2 2 0 1 1 4 0v1.5" stroke="#748094" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            {byLabel}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0 8px", height: 32 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
            {deadlineStr}
          </span>
        </div>
      </div>
      {/* "Response due:" / "Resolution due:" row — timer icon + info icon + countdown */}
      <div style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 32 }}>
        <div style={{ width: 140, display: "flex", alignItems: "center", gap: 8, flexShrink: 0, padding: "8px 0" }}>
          <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7.5" r="5.5" stroke="#748094" strokeWidth="1.3"/>
              <path d="M7 5v2.5l1.5 1.5" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.5 1.5h3" stroke="#748094" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            {dueLabel}
          </span>
          <div style={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="6.5" cy="6.5" r="5.5" stroke="#a2aab8" strokeWidth="1.2"/>
              <path d="M6.5 6v3.5" stroke="#a2aab8" strokeWidth="1.2" strokeLinecap="round"/>
              <circle cx="6.5" cy="4" r="0.7" fill="#a2aab8"/>
            </svg>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0 8px", height: 32 }}>
          {timerContent}
        </div>
      </div>
    </>
  );
}

// ─── Right panel sections ─────────────────────────────────────────────────────

function RightPanelSection({ title, children, onAssign, assignDisabled = false }: {
  title: string; children?: React.ReactNode; onAssign?: () => void; assignDisabled?: boolean;
}) {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
      <div style={{
        display: "flex", alignItems: "center", padding: "16px 16px",
        gap: 8,
      }}>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
        }}>
          {title}
        </span>
        {onAssign && (
          <button
            onClick={onAssign}
            style={{
              border: "none", background: "transparent", cursor: assignDisabled ? "default" : "pointer",
              fontSize: 12, fontWeight: 700, color: assignDisabled ? "#d1d5db" : "#0517f8",
              fontFamily: "var(--font-family-base)", padding: "4px", lineHeight: "16px",
            }}
          >
            Assign
          </button>
        )}
        <button onClick={() => setOpen((v) => !v)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
      </div>
      {open && children && <div style={{ paddingBottom: 16, paddingLeft: 16, paddingRight: 16 }}>{children}</div>}
    </div>
  );
}

function CompanyCard({ company }: { company: { name: string; id: string } }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: 12, border: "1px solid var(--color-border-primary)", borderRadius: 8,
      background: "white",
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: "#f3e8ff",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <BuildingIcon color="#9333ea" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0, lineHeight: "20px", letterSpacing: "-0.28px" }}>
          {company.name}
        </p>
        <p style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", margin: 0, lineHeight: "16px" }}>
          {company.id}
        </p>
      </div>
      <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
        <DotsIcon />
      </button>
    </div>
  );
}

function AssetCard({ asset }: { asset: { name: string; id: string } }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: 12, border: "1px solid var(--color-border-primary)", borderRadius: 8,
      background: "white",
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, background: "#e6e8fe",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <AssetIconSmall color="#6974fb" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0, lineHeight: "20px", letterSpacing: "-0.28px" }}>
          {asset.name}
        </p>
        <p style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", margin: 0, lineHeight: "16px" }}>
          {asset.id}
        </p>
      </div>
      <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
        <DotsIcon />
      </button>
    </div>
  );
}

// ─── Quote/Invoice Creation Panel ────────────────────────────────────────────

function docStatusStyle(status: string): { background: string; color: string } {
  const s = status.toUpperCase();
  if (s === "DRAFT") return { background: "#f3f4f6", color: "#748094" };
  if (s === "SENT" || s === "PENDING") return { background: "#fff0e6", color: "#ff7a00" };
  if (s === "APPROVED" || s === "PAID") return { background: "#e6f9f1", color: "#10b981" };
  if (s === "DECLINED" || s === "OVERDUE") return { background: "#fde5f0", color: "#ee0064" };
  return { background: "#f3f4f6", color: "#748094" };
}

function CreateDocumentSection({
  onCreateQuote,
  onCreateInvoice,
  linkedDocs = [],
  onOpenDoc,
}: {
  onCreateQuote: () => void;
  onCreateInvoice: () => void;
  linkedDocs?: LinkedDoc[];
  onOpenDoc?: (doc: LinkedDoc) => void;
}) {
  const [poFile, setPoFile] = React.useState<File | null>(null);
  const poInputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div style={{ borderBottom: "1px solid var(--color-border-primary)" }}>

      {/* ── Purchase Order ───────────────────────────────────────────────── */}
      <input
        ref={poInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) setPoFile(f); e.target.value = ""; }}
      />
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid var(--color-border-primary)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
              <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.28px" }}>
              Purchase Order
            </span>
          </div>
          <button
            onClick={() => poInputRef.current?.click()}
            style={{ fontSize: 12, fontWeight: 600, color: "var(--color-content-brand)", fontFamily: "var(--font-family-base)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            Attach
          </button>
        </div>
        {poFile ? (
          <div
            onClick={() => { const url = URL.createObjectURL(poFile); window.open(url, "_blank"); }}
            style={{ border: "1px solid var(--color-border-primary)", borderRadius: 6, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", transition: "background 80ms ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F9FAFB"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <div style={{ width: 28, height: 28, borderRadius: 6, background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
                <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: "18px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {poFile.name}
              </div>
              <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>Click to view</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5.5 3.5L9 7l-3.5 3.5" />
            </svg>
          </div>
        ) : (
          <div
            onClick={() => poInputRef.current?.click()}
            style={{ border: "1px dashed var(--color-border-primary)", borderRadius: 6, padding: "12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", transition: "background 80ms ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F9FAFB"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <div style={{ width: 28, height: 28, borderRadius: 6, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
                <path d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3" />
              </svg>
            </div>
            <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>No PO attached — click to attach</span>
          </div>
        )}
      </div>

      {/* ── Quotes & Invoices ────────────────────────────────────────────── */}
      <div style={{ padding: "16px 16px 0" }}>
        <span style={{
          fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", lineHeight: "20px", letterSpacing: "-0.28px",
        }}>
          Quotes & Invoices
        </span>
      </div>
      <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Linked docs */}
        {linkedDocs.map((doc) => {
          const st = docStatusStyle(doc.status);
          return (
            <div
              key={doc.number}
              onClick={() => onOpenDoc?.(doc)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", border: "1px solid var(--color-border-primary)", borderRadius: 8,
                background: "white", cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
            >
              <div style={{
                width: 28, height: 28, borderRadius: 6, background: "#e6e8fe",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                {doc.type === "quote" ? <DocumentIcon color="#6974fb" /> : <ReceiptIcon color="#6974fb" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0, lineHeight: "18px", letterSpacing: "-0.26px" }}>
                  {doc.number}
                </p>
                <p style={{ fontSize: 11, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", margin: 0 }}>
                  {doc.date}
                </p>
              </div>
              <span style={{
                padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                background: st.background, color: st.color, fontFamily: "var(--font-family-base)",
              }}>
                {doc.status}
              </span>
            </div>
          );
        })}

        {/* Action buttons */}
        <button
          onClick={onCreateQuote}
          style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%",
            padding: "10px 14px", border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "white", cursor: "pointer",
            fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
            transition: "background 80ms ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
        >
          <div style={{
            width: 24, height: 24, borderRadius: 6, background: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <DocumentIcon color="#0517f8" />
          </div>
          Create Quotation
          <div style={{ marginLeft: "auto" }}>
            <PlusIcon color="#0517f8" />
          </div>
        </button>

        <button
          onClick={onCreateInvoice}
          style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%",
            padding: "10px 14px", border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "white", cursor: "pointer",
            fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
            transition: "background 80ms ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
        >
          <div style={{
            width: 24, height: 24, borderRadius: 6, background: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ReceiptIcon color="#0517f8" />
          </div>
          Create Invoice
          <div style={{ marginLeft: "auto" }}>
            <PlusIcon color="#0517f8" />
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── Comments Tab ─────────────────────────────────────────────────────────────

function WOCommentBubble({ message }: { message: WOChatMessage }) {
  if (message.isOutgoing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
        <div style={{
          background: "#e6e8fe", borderRadius: "12px 12px 0 12px",
          padding: 12, maxWidth: 520,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              flex: 1, fontSize: 14, fontWeight: 700, lineHeight: "20px",
              letterSpacing: "-0.28px", color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)",
            }}>
              {message.sender}
            </span>
            <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
              <DotsIcon />
            </button>
          </div>
          <p style={{
            fontSize: 14, fontWeight: 500, lineHeight: "20px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0,
          }}>
            {message.text}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
              {message.time}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      <div style={{
        background: "#f3f4f6", borderRadius: "12px 12px 12px 0",
        padding: 12, maxWidth: 520,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <span style={{
          fontSize: 14, fontWeight: 700, lineHeight: "20px",
          letterSpacing: "-0.28px", color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)",
        }}>
          {message.sender}
        </span>
        <p style={{
          fontSize: 14, fontWeight: 500, lineHeight: "20px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0,
        }}>
          {message.text}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
            {message.time}
          </span>
        </div>
      </div>
    </div>
  );
}

function WOChatInput({ onSend }: { onSend: (text: string) => void }) {
  const [value, setValue] = React.useState("");
  return (
    <div style={{
      borderTop: "1px solid var(--color-border-primary)",
      padding: "16px 24px",
      display: "flex", alignItems: "center", gap: 16,
      background: "white", flexShrink: 0,
    }}>
      <input
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && value.trim()) { onSend(value.trim()); setValue(""); } }}
        placeholder="Write your message here..."
        style={{
          flex: 1, border: "none", outline: "none", background: "transparent",
          fontSize: 14, fontWeight: 500, lineHeight: "20px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <button style={{
          width: 34, height: 34, border: "1px solid var(--color-border-primary)",
          borderRadius: 8, background: "white", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <AttachmentIcon />
        </button>
        <button
          onClick={() => { if (value.trim()) { onSend(value.trim()); setValue(""); } }}
          style={{
            width: 34, height: 34, border: "none",
            borderRadius: 8, background: "#0517f8", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <WOSendIcon />
        </button>
      </div>
    </div>
  );
}

function WOCommentsTab({ messages, onSend }: { messages: WOChatMessage[]; onSend: (text: string) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Internal banner */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 8,
          background: "#fff7f0", borderRadius: 8, padding: "8px 12px",
        }}>
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <WOInfoIcon />
          </div>
          <p style={{
            fontSize: 12, fontWeight: 500, lineHeight: "16px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0,
          }}>
            These comments are internal and only visible to your team members. Customers won't see anything posted here.
          </p>
        </div>

        {/* Date separator */}
        <div style={{ textAlign: "center" }}>
          <span style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
            color: "#748094", fontFamily: "var(--font-family-base)",
          }}>
            Today, 15 April 2026
          </span>
        </div>

        {messages.map(m => <WOCommentBubble key={m.id} message={m} />)}
      </div>
      <WOChatInput onSend={onSend} />
    </div>
  );
}

// ─── Time Tracking Tab ────────────────────────────────────────────────────────

function TimeTrackingTab() {
  const grouped = React.useMemo(() => {
    const map: Record<string, TimeEntry[]> = {};
    MOCK_TIME_ENTRIES.forEach(entry => {
      if (!map[entry.date]) map[entry.date] = [];
      map[entry.date].push(entry);
    });
    return map;
  }, []);

  function calcTotal(entries: TimeEntry[]): string {
    const secs = entries.reduce((sum, e) => {
      const [h, m, s] = e.duration.split(":").map(Number);
      return sum + h * 3600 + m * 60 + s;
    }, 0);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  return (
    <div style={{ padding: "0 24px 24px" }}>
      {/* Add Time Entry */}
      <div style={{ padding: "16px 0", display: "flex", alignItems: "center" }}>
        <button style={{
          display: "flex", alignItems: "center", gap: 4,
          border: "none", background: "transparent", cursor: "pointer",
          padding: 4, fontSize: 12, fontWeight: 700,
          color: "#0517f8", fontFamily: "var(--font-family-base)",
        }}>
          <PlusIcon color="#0517f8" />
          Add Time Entry
        </button>
      </div>

      {/* Date groups */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {Object.entries(grouped).map(([date, entries]) => (
          <div key={date} style={{
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8, overflow: "hidden",
          }}>
            {/* Date header */}
            <div style={{ padding: 16 }}>
              <span style={{
                fontSize: 12, fontWeight: 500,
                color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)",
              }}>
                {date}
              </span>
            </div>

            {/* Entry rows */}
            {entries.map((entry) => (
              <div key={entry.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: 16, borderTop: "1px solid var(--color-border-primary)",
              }}>
                <TagIcon />
                <span style={{
                  fontSize: 12, fontWeight: 500,
                  color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                }}>
                  {entry.workType}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 700, color: "#748094",
                  fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                }}>
                  • {entry.category}
                </span>

                {/* Billable + assignee — flex spacer */}
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, justifyContent: "center", minWidth: 0 }}>
                  {entry.billable && <EuroIcon />}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar initials={entry.assignee.initials} size={24} />
                    <span style={{
                      fontSize: 14, fontWeight: 500,
                      color: "var(--color-content-primary)",
                      fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                    }}>
                      {entry.assignee.name}
                    </span>
                  </div>
                </div>

                {/* Duration + time range */}
                <div style={{ display: "flex", gap: 12, alignItems: "center", width: 200, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 500,
                    color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                  }}>
                    {entry.duration}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 500, color: "#748094",
                    fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                  }}>
                    {entry.timeRange}
                  </span>
                </div>

                {/* More menu */}
                <button style={{
                  border: "none", background: "transparent",
                  cursor: "pointer", padding: 0, display: "flex", flexShrink: 0,
                  color: "#748094",
                }}>
                  <DotsIcon />
                </button>
              </div>
            ))}

            {/* Total footer */}
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", padding: 16,
            }}>
              <span style={{
                fontSize: 12, fontWeight: 500, color: "#748094",
                fontFamily: "var(--font-family-base)",
              }}>
                Total Time Spent:
              </span>
              <span style={{
                fontSize: 12, fontWeight: 700,
                color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
              }}>
                {calcTotal(entries)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface WorkOrderDetailPageProps {
  workOrderId: string;
  onBack: () => void;
  onCreateQuote: (woNumber: string, woTitle: string, lineItems: WOLineItem[]) => void;
  onCreateInvoice: (woNumber: string, woTitle: string, lineItems: WOLineItem[]) => void;
  linkedDocs?: LinkedDoc[];
  onOpenLinkedDoc?: (doc: LinkedDoc) => void;
  linkedTicket?: { id: string; number: string; title: string; status: string };
  onOpenLinkedTicket?: (ticketId: string) => void;
}

const DETAIL_TABS: DetailTab[] = ["Overview", "Activity Log", "Comments", "Emails", "Time Tracking"];

function buildWOLineItems(): WOLineItem[] {
  const partItems: WOLineItem[] = MOCK_PARTS.map((p) => ({
    id: `wo-part-${p.id}`,
    name: p.name,
    qty: p.quantity,
    unit: "pcs",
    price: 0,
    discount: 0,
  }));
  const timeItems: WOLineItem[] = MOCK_TIME_ENTRIES.map((te) => {
    const [h, m] = te.duration.split(":").map(Number);
    const hours = Math.round((h + m / 60) * 100) / 100;
    return {
      id: `wo-time-${te.id}`,
      name: `${te.workType} – ${te.category}`,
      qty: hours,
      unit: "hour",
      price: 0,
      discount: 0,
    };
  });
  return [...partItems, ...timeItems];
}

export function WorkOrderDetailPage({ workOrderId: _workOrderId, onBack, onCreateQuote, onCreateInvoice, linkedDocs, onOpenLinkedDoc, linkedTicket, onOpenLinkedTicket }: WorkOrderDetailPageProps) {
  const wo = MOCK_WO;
  const [activeTab, setActiveTab] = React.useState<DetailTab>("Overview");
  const [commentMessages, setCommentMessages] = React.useState<WOChatMessage[]>(WO_COMMENT_MESSAGES);
  const [title, setTitle] = React.useState(wo.title);
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [status, setStatus] = React.useState<WOStatus>(wo.status);
  const [onsiteAt, setOnsiteAt] = React.useState<number | null>(null);
  const [closedAt, setClosedAt] = React.useState<number | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const slaStartRef = React.useRef(Date.now());

  type WOActivityType = 'created' | 'status_changed' | 'timer_started' | 'timer_stopped' | 'sla_timer_started' | 'sla_met' | 'sla_paused' | 'sla_resumed';
  interface WOActivityEntry { id: string; type: WOActivityType; message: string; timestamp: string; user?: string; }
  function woNowStamp() {
    const now = new Date();
    return now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  const [activityLog, setActivityLog] = React.useState<WOActivityEntry[]>([
    { id: 'act-init',            type: 'created',           message: 'Work order created',                         timestamp: woNowStamp(), user: 'James McDonald' },
    { id: 'act-sla-onsite-start',type: 'sla_timer_started', message: 'Onsite SLA timer started · Target 2h',       timestamp: woNowStamp() },
  ]);
  const [timerRunning, setTimerRunning] = React.useState(false);
  function addActivityEntry(type: WOActivityType, message: string, user?: string) {
    setActivityLog(prev => [{ id: `act-${Date.now()}`, type, message, timestamp: woNowStamp(), user }, ...prev]);
  }

  const WO_STATUS_LABELS: Record<WOStatus, string> = { OPEN: "Open", ONSITE: "Onsite", IN_PROGRESS: "In Progress", COMPLETED: "Completed", ON_HOLD: "On Hold", OVERDUE: "Overdue" };

  function handleStatusChange(newStatus: WOStatus) {
    addActivityEntry('status_changed', `Status changed to ${WO_STATUS_LABELS[newStatus]}`, 'James McDonald');
    if (newStatus === "ONSITE" && onsiteAt == null) {
      addActivityEntry('sla_met', 'Onsite SLA met ✓');
      addActivityEntry('sla_timer_started', 'Resolution SLA timer started · Target 24h');
    }
    if (newStatus === "COMPLETED") {
      addActivityEntry('sla_met', 'Resolution SLA met ✓');
    }
    if (newStatus === "ON_HOLD") {
      addActivityEntry('sla_paused', 'Resolution SLA paused · Work order on hold');
    }
    setStatus(newStatus);
    setStatusDropdownOpen(false);
    if (newStatus === "ONSITE" && onsiteAt == null) setOnsiteAt(Date.now());
    if (newStatus === "COMPLETED") setClosedAt(Date.now());
  }

  function handleRecordToggle() {
    if (timerRunning) {
      addActivityEntry('timer_stopped', 'Time recording stopped');
      setTimerRunning(false);
    } else {
      addActivityEntry('timer_started', 'Time recording started');
      setTimerRunning(true);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Page Header */}
      <PageHeader
        breadcrumbs={[
          { label: "Work Orders", onClick: onBack },
          { label: title },
        ]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* More options */}
            <button style={{
              width: 40, height: 40, border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <DotsHorizontalIcon />
            </button>
            {/* Export / Import */}
            <button style={{
              width: 40, height: 40, border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <ImportIcon />
            </button>
            {/* Chat */}
            <button style={{
              height: 40, padding: "0 14px", border: "1px solid var(--color-border-primary)", borderRadius: 8,
              background: "white", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
              color: "var(--color-content-primary)", letterSpacing: "-0.24px",
            }}>
              <ChatBubbleIcon />
              Chat
            </button>
            {/* Record */}
            <button
              onClick={handleRecordToggle}
              style={{
                height: 40, padding: "0 14px", border: "none", borderRadius: 8,
                background: timerRunning ? "#ee0064" : "#0517f8", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
                color: "white", letterSpacing: "-0.24px", transition: "background 150ms ease",
              }}>
              <RecordIcon />
              {timerRunning ? "Stop" : "Record"}
            </button>
          </div>
        }
      />

      {/* Two-column layout */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Left column */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflowY: "auto", background: "white" }}>
          {/* Title section */}
          <div style={{ padding: "24px 24px 0", borderBottom: "1px solid var(--color-border-primary)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
              {/* Avatar with badge */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "50%", background: "#e6e8fe",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0517f8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                  </svg>
                </div>
                {/* Globe badge */}
                <div style={{
                  position: "absolute", top: 0, right: 0,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "#f3f4f6", border: "1.5px solid white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#748094" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="5" cy="5" r="4" />
                    <path d="M1 5h8M5 1c-1.2 1.4-1.8 2.6-1.8 4s.6 2.6 1.8 4M5 1c1.2 1.4 1.8 2.6 1.8 4S6.2 11.6 5 13" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingTitle ? (
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setEditingTitle(false)}
                    autoFocus
                    style={{
                      fontSize: 20, fontWeight: 700, color: "#0f172a",
                      fontFamily: "var(--font-family-base)", letterSpacing: "-0.4px",
                      border: "none", outline: "none", background: "transparent",
                      width: "100%", lineHeight: "28px",
                    }}
                  />
                ) : (
                  <h1
                    onClick={() => setEditingTitle(true)}
                    style={{
                      fontSize: 20, fontWeight: 700, color: "#0f172a",
                      fontFamily: "var(--font-family-base)", margin: 0,
                      letterSpacing: "-0.4px", lineHeight: "28px", cursor: "text",
                    }}
                  >
                    {title}
                  </h1>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
                    {wo.number}
                  </span>
                  <span style={{ color: "#d1d5db" }}>•</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
                    Work Order
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{
              display: "flex", gap: 4, alignItems: "flex-start",
              borderBottom: "1px solid #e8eaed", marginBottom: -1,
            }}>
              {DETAIL_TABS.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      display: "flex", flexDirection: "column", gap: 12,
                      alignItems: "flex-start", border: "none", background: "transparent",
                      cursor: "pointer", padding: 0, flexShrink: 0,
                    }}
                  >
                    <div style={{ padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, lineHeight: "16px",
                        letterSpacing: "-0.24px", whiteSpace: "nowrap",
                        fontFamily: "var(--font-family-base)",
                        color: active ? "var(--color-content-primary)" : "#a2aab8",
                        transition: "color 120ms ease",
                      }}>
                        {tab}
                      </span>
                    </div>
                    <div style={{
                      height: 2, width: "100%",
                      background: active ? "#0517f8" : "transparent",
                      borderRadius: active ? "4px 4px 0 0" : 0,
                      transition: "background 120ms ease",
                    }} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {activeTab === "Overview" && (
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
                <DescriptionSection description={wo.description} />
                <ProcedureSection />
                <PartsSection />
                <AttachmentsSection />
                <LinkedWOsSection />
              </div>
            )}
            {activeTab === "Time Tracking" && <TimeTrackingTab />}
            {activeTab === "Comments" && (
              <WOCommentsTab
                messages={commentMessages}
                onSend={(text) => setCommentMessages(prev => [...prev, {
                  id: String(Date.now()), sender: "You", senderInitials: "YU",
                  text, time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
                  isOutgoing: true,
                }])}
              />
            )}
            {activeTab === "Emails" && <WOEmailsTab woNumber={wo.number} />}
            {activeTab === "Activity Log" && (
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
                {activityLog.length === 0 ? (
                  <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>No activity yet.</span>
                ) : activityLog.map((entry, idx) => {
                  const colorMap: Record<WOActivityType, string> = {
                    created:           "#F3F4F6",
                    status_changed:    "#EFF6FF",
                    timer_started:     "#D1FAE5",
                    timer_stopped:     "#FFF7ED",
                    sla_timer_started: "#EFF6FF",
                    sla_met:           "#D1FAE5",
                    sla_paused:        "#FFFBEB",
                    sla_resumed:       "#F0FDF4",
                  };
                  const iconMap: Record<WOActivityType, React.ReactNode> = {
                    created: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="2" /><path d="M4 6h4M6 4v4" /></svg>
                    ),
                    status_changed: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M4 6h4M7 4.5L8.5 6 7 7.5" /></svg>
                    ),
                    timer_started: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6.5" r="4.5" /><path d="M6 4.5v2l1.5 1" /><path d="M4.5 1.5h3" /></svg>
                    ),
                    timer_stopped: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#EA580C" strokeWidth="1.4" strokeLinecap="round"><circle cx="6" cy="6.5" r="4.5" /><rect x="4" y="4.5" width="4" height="4" rx="0.5" fill="#EA580C" stroke="none" /></svg>
                    ),
                    sla_timer_started: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#2563EB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6.5" r="4.5" /><path d="M6 4.5v2l1.5 1" /><path d="M4.5 1.5h3" /></svg>
                    ),
                    sla_met: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M3.5 6l2 2 3-3" /></svg>
                    ),
                    sla_paused: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" strokeWidth="1.4" strokeLinecap="round"><rect x="2" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" /><rect x="7.5" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" /></svg>
                    ),
                    sla_resumed: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2l7 4-7 4V2z" fill="#059669" stroke="none" /></svg>
                    ),
                  };
                  return (
                    <div key={entry.id} style={{ display: "flex", gap: 10, position: "relative" }}>
                      {idx < activityLog.length - 1 && (
                        <div style={{ position: "absolute", left: 11, top: 24, bottom: -4, width: 1, background: "#E5E7EB" }} />
                      )}
                      <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: colorMap[entry.type], display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, zIndex: 1 }}>
                        {iconMap[entry.type]}
                      </div>
                      <div style={{ flex: 1, paddingBottom: idx < activityLog.length - 1 ? 14 : 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: 1.4 }}>{entry.message}</div>
                        {entry.user && <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", marginTop: 1 }}>{entry.user}</div>}
                        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "var(--font-family-base)", marginTop: 2 }}>{entry.timestamp}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{
          width: 340, flexShrink: 0,
          borderLeft: "1px solid var(--color-border-primary)",
          overflowY: "auto", background: "white",
        }}>
          {/* WO Details Block */}
          <RightPanelSection title="Details">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DetailRow icon={<StatusIcon />} label="Status">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", position: "relative" }}>
                  <div style={{ cursor: "pointer" }} onClick={() => setStatusDropdownOpen(v => !v)}>
                    <StatusBadge status={status} />
                  </div>
                  {statusDropdownOpen && (
                    <div style={{
                      position: "absolute", top: "100%", left: 8, zIndex: 100,
                      background: "white", border: "1px solid var(--color-border-primary)",
                      borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      minWidth: 140, overflow: "hidden",
                    }}>
                      {(["OPEN", "ONSITE", "IN_PROGRESS", "COMPLETED", "ON_HOLD"] as WOStatus[]).map(s => (
                        <div
                          key={s}
                          onClick={() => handleStatusChange(s)}
                          style={{
                            padding: "8px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600,
                            fontFamily: "var(--font-family-base)",
                            color: STATUS_CONFIG[s].color,
                            background: status === s ? STATUS_CONFIG[s].bg : "transparent",
                          }}
                          onMouseEnter={e => { if (status !== s) e.currentTarget.style.background = "#f9fafb"; }}
                          onMouseLeave={e => { if (status !== s) e.currentTarget.style.background = "transparent"; }}
                        >
                          {STATUS_CONFIG[s].label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DetailRow>

              <DetailRow icon={<UsersIcon />} label="Assignee">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", gap: 4 }}>
                  <AvatarStack users={wo.assignees} />
                </div>
              </DetailRow>

              <DetailRow icon={<UsersIcon />} label="Team">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center" }}>
                  <span style={{
                    padding: "4px 6px", borderRadius: 4, background: "#f3f4f6",
                    fontSize: 12, fontWeight: 700, color: "#748094",
                    fontFamily: "var(--font-family-base)",
                  }}>
                    {wo.team}
                  </span>
                </div>
              </DetailRow>

              <DetailRow icon={<CalendarIcon />} label="Due Date">
                <DetailValueText>{wo.dueDate}</DetailValueText>
              </DetailRow>

              <DetailRow icon={<CalendarIcon />} label="Schedule">
                <DetailValueText>{wo.schedule}</DetailValueText>
              </DetailRow>

              <DetailRow icon={<UserIcon />} label="Company Contact">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar initials={wo.companyContact.initials} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                    {wo.companyContact.name}
                  </span>
                </div>
              </DetailRow>

              <DetailRow icon={<UsersIcon />} label="Followers">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", gap: 4 }}>
                  <AvatarStack users={wo.followers} />
                </div>
              </DetailRow>

              <DetailRow icon={<ClipboardIcon />} label="Resources">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ padding: "4px 6px", borderRadius: 4, background: "#f6f7ff", fontSize: 12, fontWeight: 700, color: "#6974fb", fontFamily: "var(--font-family-base)" }}>CUSTOM</span>
                  <span style={{ padding: "4px 6px", borderRadius: 4, background: "#f6f7ff", fontSize: 12, fontWeight: 700, color: "#6974fb", fontFamily: "var(--font-family-base)" }}>+1</span>
                </div>
              </DetailRow>

              <DetailRow icon={<CalendarIcon />} label="Created at">
                <DetailValueText>{wo.createdAt}</DetailValueText>
              </DetailRow>

              <DetailRow icon={<UserIcon />} label="Created by">
                <div style={{ padding: "0 8px", height: 32, display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar initials={wo.createdBy.initials} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                    {wo.createdBy.name}
                  </span>
                </div>
              </DetailRow>

              <DetailRow icon={<IdIcon />} label="Work Order ID">
                <DetailValueText>{wo.number}</DetailValueText>
              </DetailRow>
            </div>
          </RightPanelSection>

          {/* SLA Block */}
          <RightPanelSection title="SLA">
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {linkedTicket && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", marginBottom: 4,
                  background: "#f0f4ff", borderRadius: 6,
                  border: "1px solid #d0d8f8",
                }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 9l-5 5a1.5 1.5 0 01-2.1 0L1 8V1h7l6 6a1.5 1.5 0 010 2z" />
                    <circle cx="4.5" cy="4.5" r="0.8" fill="#0517f8" stroke="none" />
                  </svg>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#0517f8", fontFamily: "var(--font-family-base)" }}>
                    SLAs started from linked ticket
                  </span>
                </div>
              )}
              <SLARow
                byLabel="Onsite by:"
                dueLabel="Onsite due:"
                deadlineStr="12/06/2026 08:27 am"
                state={onsiteAt != null || status === "ONSITE" || status === "COMPLETED" ? "met" : "running"}
                startedAt={slaStartRef.current}
                mountElapsed={1 * 60 + 45}
                targetMinutes={2 * 60}
              />
              <SLARow
                byLabel="Resolution by:"
                dueLabel="Resolution due:"
                deadlineStr="12/06/2026 12:27 pm"
                state={closedAt != null || status === "COMPLETED" ? "met" : onsiteAt != null ? "running" : "idle"}
                startedAt={onsiteAt ?? undefined}
                mountElapsed={0}
                targetMinutes={24 * 60}
              />
              {linkedTicket && (
                <div
                  onClick={() => onOpenLinkedTicket?.(linkedTicket.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", marginTop: 4,
                    background: "#fffbeb", borderRadius: 6,
                    border: "1px solid #fde68a",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <rect x="4" y="3" width="2" height="6" rx="1" fill="#d97706" />
                      <rect x="8" y="3" width="2" height="6" rx="1" fill="#d97706" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#92400e", fontFamily: "var(--font-family-base)", letterSpacing: "-0.1px" }}>
                      Ticket Resolution SLA
                    </p>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 500, color: "#b45309", fontFamily: "var(--font-family-base)" }}>
                      Paused while this WO is open
                    </p>
                  </div>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#b45309" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 2.5L8 6l-3.5 3.5" />
                  </svg>
                </div>
              )}
            </div>
          </RightPanelSection>

          {/* Tickets Block */}
          <RightPanelSection title="Tickets">
            {linkedTicket ? (
              <div
                onClick={() => onOpenLinkedTicket?.(linkedTicket.id)}
                style={{
                  border: "1px solid var(--color-border-primary)", borderRadius: 8,
                  padding: 12, display: "flex", alignItems: "center", gap: 12,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: "#e6e8fe",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 9l-5 5a1.5 1.5 0 01-2.1 0L1 8V1h7l6 6a1.5 1.5 0 010 2z" />
                    <circle cx="4.5" cy="4.5" r="0.8" fill="#0517f8" stroke="none" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: 14, fontWeight: 700, lineHeight: "20px",
                    letterSpacing: "-0.28px", color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {linkedTicket.title}
                  </p>
                  <p style={{
                    margin: 0, fontSize: 12, fontWeight: 500, color: "#748094",
                    fontFamily: "var(--font-family-base)",
                  }}>
                    {linkedTicket.number}
                  </p>
                </div>
                <span style={{
                  padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#ccefeb", color: "#00b09b",
                  fontFamily: "var(--font-family-base)", flexShrink: 0,
                }}>
                  {linkedTicket.status}
                </span>
              </div>
            ) : (
              <div style={{
                textAlign: "center", padding: "16px 0",
                fontSize: 12, fontWeight: 500, color: "#a2aab8",
                fontFamily: "var(--font-family-base)",
              }}>
                No tickets linked
              </div>
            )}
          </RightPanelSection>

          {/* Company Block */}
          <RightPanelSection title="Company" onAssign={() => {}} assignDisabled>
            <CompanyCard company={wo.company} />
          </RightPanelSection>

          {/* Assets Block */}
          <RightPanelSection title="Assets" onAssign={() => {}}>
            <AssetCard asset={wo.asset} />
          </RightPanelSection>

          {/* Quotes & Invoices Block */}
          <CreateDocumentSection
            onCreateQuote={() => onCreateQuote(wo.number, wo.title, buildWOLineItems())}
            onCreateInvoice={() => onCreateInvoice(wo.number, wo.title, buildWOLineItems())}
            linkedDocs={linkedDocs}
            onOpenDoc={onOpenLinkedDoc}
          />

          {/* WO Custom Fields */}
          <RightPanelSection title="Work Order Custom Fields">
            <div style={{ fontSize: 12, fontWeight: 500, color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>
              No custom fields configured
            </div>
          </RightPanelSection>
        </div>
      </div>
    </div>
  );
}
